'use client';

/**
 * The single stateful boundary of the Kids module.
 *
 * Everything below it is pure (curriculum, engine, metrics, analytics) and
 * everything above it is presentation. This component owns exactly three
 * responsibilities:
 *
 *   1. Read the store once on mount and expose a `hydrated` flag. Nothing that
 *      depends on saved progress may render before that flag flips, because
 *      localStorage does not exist during server rendering and rendering it
 *      differently on the client is a hydration error.
 *   2. Apply every mutation through a pure helper, then persist.
 *   3. Keep the accessibility/appearance settings mirrored onto the DOM so the
 *      scoped CSS theme can react to them.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  AvatarConfig,
  KidProfile,
  KidSettings,
  KidsStore,
  LessonScore,
  ShopItem,
} from '../types';
import {
  createProfile as makeProfile,
  emptyStore,
  getActiveProfile,
  loadStore,
  recordLesson,
  saveStore,
  updateProfile as applyToProfile,
  dayKey,
} from '../lib/progress';
import { evaluateAchievements, getAchievement } from '../data/achievements';
import { chestReward } from '../data/shop';
import { rankFor } from '../lib/metrics';
import { kidsAudio } from '../lib/audio';

export interface FinishOutcome {
  firstClear: boolean;
  improved: boolean;
  /** Achievement ids earned by this run, in display order. */
  newAchievements: string[];
  /** Set when the run pushed the child into a new learner rank. */
  rankUp: { from: string; to: string; emoji: string } | null;
  coinsEarned: number;
  xpEarned: number;
}

interface KidsContextValue {
  /** False until the saved store has been read on the client. */
  hydrated: boolean;
  store: KidsStore;
  profile: KidProfile | null;
  /** Convenience — the same object the whole app derives its look from. */
  settings: KidSettings;

  createProfile: (name: string, ageBand?: KidProfile['ageBand']) => string;
  switchProfile: (id: string) => void;
  renameProfile: (id: string, name: string) => void;
  deleteProfile: (id: string) => void;
  resetProfile: (id: string) => void;

  updateSettings: (patch: Partial<KidSettings>) => void;
  updateAvatar: (patch: Partial<AvatarConfig>) => void;

  finishLesson: (
    lessonId: string,
    score: LessonScore,
    keyStats: Record<string, { correct: number; wrong: number }>
  ) => FinishOutcome;

  buyItem: (item: ShopItem) => { ok: boolean; reason?: string };
  equipItem: (item: ShopItem) => void;
  claimChest: () => { coins: number; label: string; emoji: string } | null;
  canClaimChest: boolean;

  assignLesson: (lessonId: string, assigned: boolean) => void;
  setClassroom: (classroom: { name: string; teacher: string } | null) => void;

  exportData: () => string;
  importData: (json: string) => boolean;
}

const KidsContext = createContext<KidsContextValue | null>(null);

export function KidsProvider({ children }: { children: React.ReactNode }) {
  const [store, setStore] = useState<KidsStore>(emptyStore);
  const [hydrated, setHydrated] = useState(false);
  const storeRef = useRef(store);
  storeRef.current = store;

  /** Write-through: every mutation goes through here, so nothing forgets to save. */
  const commit = useCallback((next: KidsStore) => {
    storeRef.current = next;
    setStore(next);
    saveStore(next);
  }, []);

  useEffect(() => {
    setStore(loadStore());
    setHydrated(true);
  }, []);

  const profile = useMemo(() => getActiveProfile(store), [store]);
  const settings = profile?.settings ?? loadDefaultSettings();

  /* ── mirror appearance settings onto the DOM ────────────────────────────
     The scoped theme in styles/kids.css keys off these data attributes. They
     live on the <html> element rather than a wrapper so a portalled dialog or
     a print sheet inherits them too. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-kids-scheme', settings.scheme);
    root.setAttribute('data-kids-motion', settings.motion);
    root.setAttribute('data-kids-reading', settings.reading);
    root.setAttribute('data-kids-contrast', settings.contrast);
    root.setAttribute('data-kids-vision', settings.vision);
    return () => {
      for (const attr of ['scheme', 'motion', 'reading', 'contrast', 'vision']) {
        root.removeAttribute(`data-kids-${attr}`);
      }
    };
  }, [settings.scheme, settings.motion, settings.reading, settings.contrast, settings.vision]);

  useEffect(() => {
    kidsAudio.configure({
      sound: settings.sound,
      volume: settings.volume,
      voice: settings.voice,
    });
  }, [settings.sound, settings.volume, settings.voice]);

  /* ── profile lifecycle ─────────────────────────────────────────────────── */

  const createProfile = useCallback(
    (name: string, ageBand: KidProfile['ageBand'] = 'unspecified') => {
      const next = makeProfile(name, ageBand);
      commit({
        ...storeRef.current,
        profiles: [...storeRef.current.profiles, next],
        activeProfileId: next.id,
      });
      return next.id;
    },
    [commit]
  );

  const switchProfile = useCallback(
    (id: string) => commit({ ...storeRef.current, activeProfileId: id }),
    [commit]
  );

  const renameProfile = useCallback(
    (id: string, name: string) =>
      commit(
        applyToProfile(storeRef.current, id, (p) => ({
          ...p,
          name: name.trim().slice(0, 24) || p.name,
        }))
      ),
    [commit]
  );

  const deleteProfile = useCallback(
    (id: string) => {
      const profiles = storeRef.current.profiles.filter((p) => p.id !== id);
      commit({
        ...storeRef.current,
        profiles,
        activeProfileId:
          storeRef.current.activeProfileId === id
            ? (profiles[0]?.id ?? null)
            : storeRef.current.activeProfileId,
      });
    },
    [commit]
  );

  const resetProfile = useCallback(
    (id: string) =>
      commit(
        applyToProfile(storeRef.current, id, (p) => ({
          ...makeProfile(p.name, p.ageBand),
          id: p.id,
          created: p.created,
          avatar: p.avatar,
          settings: p.settings,
        }))
      ),
    [commit]
  );

  /* ── settings & avatar ─────────────────────────────────────────────────── */

  const updateSettings = useCallback(
    (patch: Partial<KidSettings>) => {
      const id = storeRef.current.activeProfileId;
      if (!id) return;
      commit(
        applyToProfile(storeRef.current, id, (p) => ({
          ...p,
          settings: { ...p.settings, ...patch },
        }))
      );
    },
    [commit]
  );

  const updateAvatar = useCallback(
    (patch: Partial<AvatarConfig>) => {
      const id = storeRef.current.activeProfileId;
      if (!id) return;
      commit(
        applyToProfile(storeRef.current, id, (p) => ({ ...p, avatar: { ...p.avatar, ...patch } }))
      );
    },
    [commit]
  );

  /* ── the core write: finishing a lesson ────────────────────────────────── */

  const finishLesson = useCallback<KidsContextValue['finishLesson']>(
    (lessonId, score, keyStats) => {
      const current = getActiveProfile(storeRef.current);
      if (!current) {
        return {
          firstClear: false,
          improved: false,
          newAchievements: [],
          rankUp: null,
          coinsEarned: 0,
          xpEarned: 0,
        };
      }

      const result = recordLesson(current, lessonId, score, keyStats);
      let next = result.profile;

      // Achievements are evaluated *after* the lesson is folded in, so a badge
      // that depends on this very run (first clear, streak, star total) fires
      // on the same screen the child is already looking at.
      const earned = evaluateAchievements(next);
      if (earned.length) {
        const now = new Date().toISOString();
        const bonus = earned.reduce((sum, id) => sum + (getAchievement(id)?.coins ?? 0), 0);
        next = {
          ...next,
          coins: next.coins + bonus,
          achievements: {
            ...next.achievements,
            ...Object.fromEntries(earned.map((id) => [id, now])),
          },
        };
      }

      const before = rankFor(result.previousRankXp);
      const after = rankFor(next.xp);

      commit(applyToProfile(storeRef.current, current.id, () => next));

      return {
        firstClear: result.firstClear,
        improved: result.improved,
        newAchievements: earned,
        rankUp:
          after.rank.level > before.rank.level
            ? { from: before.rank.name, to: after.rank.name, emoji: after.rank.emoji }
            : null,
        coinsEarned: next.coins - current.coins,
        xpEarned: next.xp - current.xp,
      };
    },
    [commit]
  );

  /* ── shop & chest ──────────────────────────────────────────────────────── */

  const buyItem = useCallback<KidsContextValue['buyItem']>(
    (item) => {
      const current = getActiveProfile(storeRef.current);
      if (!current) return { ok: false, reason: 'No profile selected.' };
      if (current.unlocked.includes(item.id)) return { ok: false, reason: 'Already unlocked.' };
      if (item.requiresRank && rankFor(current.xp).rank.level < item.requiresRank) {
        return { ok: false, reason: `Reach rank ${item.requiresRank} to unlock this.` };
      }
      if (current.coins < item.cost) {
        return { ok: false, reason: `You need ${item.cost - current.coins} more coins.` };
      }
      commit(
        applyToProfile(storeRef.current, current.id, (p) => ({
          ...p,
          coins: p.coins - item.cost,
          unlocked: [...p.unlocked, item.id],
        }))
      );
      return { ok: true };
    },
    [commit]
  );

  const equipItem = useCallback<KidsContextValue['equipItem']>(
    (item) => {
      const id = storeRef.current.activeProfileId;
      if (!id) return;
      commit(
        applyToProfile(storeRef.current, id, (p) => {
          switch (item.category) {
            case 'keyboard':
              return { ...p, equipped: { ...p.equipped, keyboardSkin: item.value } };
            case 'title':
              return {
                ...p,
                equipped: {
                  ...p.equipped,
                  title: p.equipped.title === item.value ? '' : item.value,
                },
              };
            case 'pet':
              return {
                ...p,
                avatar: { ...p.avatar, pet: p.avatar.pet === item.value ? 'none' : item.value },
              };
            case 'avatar':
            default: {
              // Look items are grouped by what they replace, inferred from the id.
              const slot = item.id.startsWith('hair-')
                ? 'hair'
                : item.id.startsWith('bg-')
                  ? 'background'
                  : 'accessory';
              const isEquipped = p.avatar[slot as keyof AvatarConfig] === item.value;
              return {
                ...p,
                avatar: {
                  ...p.avatar,
                  [slot]: isEquipped && slot === 'accessory' ? 'none' : item.value,
                },
              };
            }
          }
        })
      );
    },
    [commit]
  );

  const canClaimChest = Boolean(profile) && profile?.chestDay !== dayKey();

  const claimChest = useCallback(() => {
    const current = getActiveProfile(storeRef.current);
    const today = dayKey();
    if (!current || current.chestDay === today) return null;
    const reward = chestReward(current.streak.current);
    commit(
      applyToProfile(storeRef.current, current.id, (p) => ({
        ...p,
        coins: p.coins + reward.coins,
        chestDay: today,
      }))
    );
    return reward;
  }, [commit]);

  /* ── classroom ─────────────────────────────────────────────────────────── */

  const assignLesson = useCallback(
    (lessonId: string, assigned: boolean) => {
      const id = storeRef.current.activeProfileId;
      if (!id) return;
      commit(
        applyToProfile(storeRef.current, id, (p) => ({
          ...p,
          assigned: assigned
            ? Array.from(new Set([...p.assigned, lessonId]))
            : p.assigned.filter((x) => x !== lessonId),
        }))
      );
    },
    [commit]
  );

  const setClassroom = useCallback(
    (classroom: { name: string; teacher: string } | null) =>
      commit({ ...storeRef.current, classroom }),
    [commit]
  );

  /* ── portability — the child owns their data ───────────────────────────── */

  const exportData = useCallback(() => JSON.stringify(storeRef.current, null, 2), []);

  const importData = useCallback(
    (json: string) => {
      try {
        const parsed = JSON.parse(json) as KidsStore;
        if (!parsed || !Array.isArray(parsed.profiles)) return false;
        saveStore(parsed);
        commit(loadStore()); // round-trip through migrate() so imports are validated
        return true;
      } catch {
        return false;
      }
    },
    [commit]
  );

  const value = useMemo<KidsContextValue>(
    () => ({
      hydrated,
      store,
      profile,
      settings,
      createProfile,
      switchProfile,
      renameProfile,
      deleteProfile,
      resetProfile,
      updateSettings,
      updateAvatar,
      finishLesson,
      buyItem,
      equipItem,
      claimChest,
      canClaimChest,
      assignLesson,
      setClassroom,
      exportData,
      importData,
    }),
    [
      hydrated,
      store,
      profile,
      settings,
      createProfile,
      switchProfile,
      renameProfile,
      deleteProfile,
      resetProfile,
      updateSettings,
      updateAvatar,
      finishLesson,
      buyItem,
      equipItem,
      claimChest,
      canClaimChest,
      assignLesson,
      setClassroom,
      exportData,
      importData,
    ]
  );

  return <KidsContext.Provider value={value}>{children}</KidsContext.Provider>;
}

export function useKids(): KidsContextValue {
  const ctx = useContext(KidsContext);
  if (!ctx) throw new Error('useKids must be used inside <KidsProvider>');
  return ctx;
}

function loadDefaultSettings(): KidSettings {
  return {
    sound: true,
    voice: false,
    volume: 0.5,
    scheme: 'light',
    motion: 'full',
    reading: 'default',
    contrast: 'default',
    vision: 'default',
    keyboard: true,
    hands: true,
  };
}
